import { GlobalNavigationItem, GlobalNavigationBarMode } from "./navigationItem";
import * as busyIndicator from "client_legacy/ndaw/busyIndicator";
import { AppType, DestinationWindow } from "client/Api/Apps/common";
import { navigateToDocumentsApp, navigateToEmbedApp } from "client_legacy/Apps/appNavigation";
import { alertBar } from "client_legacy/ndaw/alertBar";
import { localize } from "client_legacy/ndaw/culture";
import {
	GlobalNavigationItemModel,
	NavigationItemConditionalAccessRestrictionModel
} from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import { MaintenanceWindowAppModel } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { FontIconName } from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { getAlertManagerOutsideReact } from "client/Hooks";
import { generateMaintenanceMessage } from "client/Features/Workspace/useTileTooltip";

interface AppNavigationItemOptions {
	id: number;
	name: string;
	model: GlobalNavigationItemModel;
	installedAppId: number;
	appType: AppType;
	conditionalAccess: OptionalModel<NavigationItemConditionalAccessRestrictionModel>;
	maintenanceWindow: OptionalModel<MaintenanceWindowAppModel>;
}

export type DisabledStatus = {
	icon: Extract<FontIconName, "ban" | "screwdriver-wrench">;
	reason: string;
};

class AppNavigationItem implements GlobalNavigationItem {
	public readonly id: number;
	public readonly name: string;
	public readonly model: GlobalNavigationItemModel;
	public readonly installedAppId: number;
	public readonly appType: AppType;
	public readonly disabledStatus: OptionalModel<DisabledStatus>;

	constructor(options: AppNavigationItemOptions, timezone: TimeZone) {
		this.id = options.id;
		this.name = options.name;
		this.model = options.model;
		this.installedAppId = options.installedAppId;
		this.appType = options.appType;

		if (options.conditionalAccess.isSet) {
			this.disabledStatus = {
				isSet: true,
				value: {
					icon: "ban",
					reason: options.conditionalAccess
						.value
						.reason
				}
			};
		} else if (options.maintenanceWindow.isSet) {
			const maintenanceWindow: MaintenanceWindowAppModel = options.maintenanceWindow
				.value;

			this.disabledStatus = {
				isSet: true,
				value: {
					icon: "screwdriver-wrench",
					reason: generateMaintenanceMessage(
						"space",
						maintenanceWindow,
						timezone
					)
				}
			};
		} else {
			this.disabledStatus = { isSet: false };
		}
	}

	public activate(_mode: GlobalNavigationBarMode) {
		if (this.disabledStatus.isSet) {
			const status: DisabledStatus = this.disabledStatus
				.value;

			getAlertManagerOutsideReact()
				.showInfo(status.reason);
			return;
		}

		busyIndicator.show("#main-content");
		const id: number = this.installedAppId;
		switch (this.appType) {
			case AppType.Documents:
				navigateToDocumentsApp({ id, destinationWindow: DestinationWindow.CurrentWindow });
				break;
			case AppType.Embed:
				navigateToEmbedApp({ id });
				break;
			default:
				console.error(`We don't have any way we support opening app '${JSON.stringify(this)}'`);
				alertBar.showTextAlert(localize("_GeneralErrorMessage"), "error");
				break;
		}
	}

	public canActivate(mode: GlobalNavigationBarMode) {
		return mode !== GlobalNavigationBarMode.EditSpaceMode;
	}

	public canDeactivate(mode: GlobalNavigationBarMode): Promise<boolean> {
		if (mode !== GlobalNavigationBarMode.Normal) {
			return Promise.reject("Modes other then Normal are not supported");
		}

		return Promise.resolve(true);
	}
}

export {
	AppNavigationItem
};